import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { FloorPlanId, ProjectId, UnitPositionId, UnitTypeId } from '../shared/ids';
import { isCommonArea, UNIT_TYPE_KIND_LABEL, type UnitType, type UnitTypeKind } from './unit-type';
import { TAKEOFF_ERROR } from './errors';

/**
 * Pavimento tipo — o desenho que se repete.
 *
 * O engenheiro monta **um**, com as suas posições de apto e a área comum, e diz de que
 * andar a que andar ele vale. Vinte pavimentos compartilham o mesmo desenho; o que existe
 * vinte vezes é o apto, não o quantitativo (RN-167).
 *
 * O `kind` não é rótulo de tela: é ele que diz se o pavimento **tem** apto. Garagem,
 * fundação e canteiro não têm, e continuam tendo orçamento — a linha pendura no próprio
 * pavimento (RN-174).
 */
export type FloorPlanKind =
  | 'SUBSOLO'
  | 'TERREO'
  | 'GARAGEM'
  | 'LAZER'
  | 'GARDEN'
  | 'TIPO'
  | 'COBERTURA'
  | 'FUNDACAO'
  // A contenção é um pavimento próprio desde a Fase 23, e desde a Fase 27 tem desenho: o
  // pilar de contenção e o muro são traçados nela (F4).
  | 'CONTENCAO'
  | 'CANTEIRO';

/** A ordem em que o prédio é montado, de baixo para cima. */
export const FLOOR_PLAN_KIND_ORDER: readonly FloorPlanKind[] = [
  // A fundação e a contenção vêm primeiro: são o que se executa antes de tudo, e desde a
  // Fase 27 têm desenho — a estaca, o bloco, o muro (F4). Fora daqui, o contrato recusava
  // criar o desenho delas e a bancada não mostrava a tipologia.
  'FUNDACAO',
  'CONTENCAO',
  'SUBSOLO',
  'TERREO',
  'GARAGEM',
  'LAZER',
  'GARDEN',
  'TIPO',
  'COBERTURA',
];

export const FLOOR_PLAN_KIND_LABEL: Readonly<Record<FloorPlanKind, string>> = {
  SUBSOLO: 'Subsolo',
  TERREO: 'Térreo',
  GARAGEM: 'Garagem',
  LAZER: 'Lazer',
  GARDEN: 'Garden',
  TIPO: 'Pavimento tipo',
  COBERTURA: 'Cobertura',
  FUNDACAO: 'Fundação',
  CONTENCAO: 'Contenção',
  CANTEIRO: 'Canteiro',
};

/**
 * Este tipo de pavimento **sempre** tem apartamento?
 *
 * O tipo tem, por definição — é o andar que se repete porque os aptos dele se repetem.
 * Garden também: são apartamentos especiais, e é só por isso que o pavimento existe.
 * Nos outros, quem cadastra decide (RN-179).
 */
export function alwaysHasUnits(kind: FloorPlanKind): boolean {
  return kind === 'TIPO' || kind === 'GARDEN';
}

/**
 * Esta tipologia cabe neste pavimento?
 *
 * A pergunta era só sobre o pavimento — "comporta apto?" — e a resposta valia para tudo
 * que se tentasse pendurar nele. Era grosseira demais em uma ponta e larga demais na
 * outra: recusava a **área comum** da garagem, que é a rampa e a circulação e existe de
 * verdade, e não tinha como recusar uma **vaga** no pavimento tipo. Agora são três
 * respostas, uma por tipo:
 *
 * - **Área comum**: em qualquer pavimento. Não existe andar sem circulação.
 * - **Apartamento**: em nenhum dos que não têm apto — garagem, subsolo, fundação e
 *   canteiro. É a regra de antes, intacta: o "apto fake" que existe só para caber no
 *   modelo continua barrado, porque ele some do relatório somando área privativa que
 *   não existe.
 * - **Vaga**: em qualquer um menos o pavimento tipo. O tipo é o andar dos apartamentos,
 *   e vaga nele seria a mesma mentira ao contrário.
 */
export function allowsUnitKind(floorKind: FloorPlanKind, unitKind: UnitTypeKind): boolean {
  if (unitKind === 'AREA_COMUM') return true;
  if (unitKind === 'VAGA') return floorKind !== 'TIPO';
  return (
    floorKind !== 'GARAGEM' &&
    floorKind !== 'FUNDACAO' &&
    floorKind !== 'CONTENCAO' &&
    floorKind !== 'CANTEIRO' &&
    floorKind !== 'SUBSOLO'
  );
}

export interface FloorPlan {
  readonly id: FloorPlanId;
  readonly projectId: ProjectId;
  readonly name: string;
  readonly code?: string;
  readonly kind: FloorPlanKind;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/**
 * Uma posição de apto dentro do pavimento tipo.
 *
 * Existe separada da tipologia porque **duas posições podem usar a mesma tipologia** — os
 * aptos 01 e 03, ambos Tipologia A. Sem a posição, a divisa entre eles não teria de quem
 * ser cada metade (RN-163), e o apto de canto não teria onde divergir.
 */
export interface UnitPosition {
  readonly id: UnitPositionId;
  readonly floorPlanId: FloorPlanId;
  readonly unitTypeId: UnitTypeId;
  /** "01", "02", "Área comum", "Vagas" — o que a planta chama. */
  readonly label: string;
  readonly order: number;
  /**
   * Quantas vezes esta posição existe no pavimento.
   *
   * **Um é o caso comum**, e é todo apartamento: o apto 01 é uma posição, o 02 é outra, e
   * eles precisam existir separados porque a divisa entre os dois tem de ser de alguém
   * (RN-163). Quarenta vagas iguais não têm essa necessidade — vaga é vaga —, e quarenta
   * posições para dizer isso seriam quarenta cadastros à mão para um número.
   */
  readonly count: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateFloorPlanInput {
  readonly id: FloorPlanId;
  readonly projectId: ProjectId;
  readonly name: string;
  readonly kind: FloorPlanKind;
  readonly code?: string;
  readonly notes?: string;
  readonly now: string;
}

export function createFloorPlan(input: CreateFloorPlanInput): Result<FloorPlan, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(
      domainError(TAKEOFF_ERROR.ROOM_NAME_REQUIRED, 'O nome do pavimento tipo é obrigatório.'),
    );
  }
  return ok({
    id: input.id,
    projectId: input.projectId,
    name,
    kind: input.kind,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

export interface CreateUnitPositionInput {
  readonly id: UnitPositionId;
  readonly floorPlan: Pick<FloorPlan, 'id' | 'kind'>;
  readonly unitTypeId: UnitTypeId;
  /** O que está sendo pendurado — é ele, com o pavimento, que decide se cabe. */
  readonly unitTypeKind: UnitTypeKind;
  readonly label: string;
  readonly order: number;
  /** Ausente é uma vez, que é todo apartamento e toda área comum. */
  readonly count?: number;
  readonly now: string;
}

export function createUnitPosition(
  input: CreateUnitPositionInput,
): Result<UnitPosition, DomainError> {
  if (!allowsUnitKind(input.floorPlan.kind, input.unitTypeKind)) {
    return err(
      domainError(
        TAKEOFF_ERROR.POSITION_NOT_ALLOWED,
        `${FLOOR_PLAN_KIND_LABEL[input.floorPlan.kind]} não comporta ${UNIT_TYPE_KIND_LABEL[input.unitTypeKind].toLowerCase()}.`,
        { kind: input.floorPlan.kind, unitKind: input.unitTypeKind },
      ),
    );
  }
  const label = input.label.trim();
  if (label.length === 0) {
    return err(domainError(TAKEOFF_ERROR.ROOM_NAME_REQUIRED, 'A posição precisa de um rótulo.'));
  }
  const count = Math.trunc(input.count ?? 1);
  if (!Number.isFinite(count) || count < 1) {
    return err(
      domainError(
        TAKEOFF_ERROR.POSITION_COUNT_INVALID,
        'A quantidade da posição precisa ser um número inteiro de 1 para cima.',
        { count: input.count },
      ),
    );
  }
  return ok({
    id: input.id,
    floorPlanId: input.floorPlan.id,
    unitTypeId: input.unitTypeId,
    label,
    order: input.order,
    count,
    createdAt: input.now,
    updatedAt: input.now,
  });
}

/**
 * O rótulo da próxima posição livre do desenho.
 *
 * O rótulo é **a vaga no desenho**, e não o nome da tipologia: "01" e "02" são os aptos
 * daquele andar, e os dois podem usar a mesma Tipologia A. São coisas diferentes, e o
 * sistema as confundia — a posição nascia com o nome da tipologia, e o nome nunca mais
 * era o mesmo: renomear a tipologia não mexia no rótulo, que ficava para trás.
 *
 * O estrago aparecia uma tipologia depois. Batizada "Apartamento 1" e renomeada para
 * "Tipologia 2", ela deixava um rótulo "Apartamento 1" órfão no desenho; a tipologia
 * seguinte nascia com o nome "Apartamento 1" — livre, porque nome de tipologia é outra
 * lista — e batia no rótulo que ninguém via. A tela recusava a segunda tipologia do
 * pavimento com uma mensagem sobre um nome que a pessoa não escolheu.
 *
 * Derivando do desenho, os dois deixam de se perseguir: o rótulo numera o que existe
 * naquele pavimento, e o nome da tipologia é livre para mudar quantas vezes for preciso.
 */
export function nextPositionLabel(
  unitKind: UnitTypeKind,
  existentes: readonly Pick<UnitPosition, 'label'>[],
): string {
  const usados = new Set(existentes.map((posicao) => normalizeName(posicao.label)));
  for (let numero = 1; numero <= existentes.length + 1; numero += 1) {
    const rotulo = rotuloDe(unitKind, numero);
    if (!usados.has(normalizeName(rotulo))) return rotulo;
  }
  // Inalcançável: entre `existentes.length + 1` rótulos, ao menos um está livre — há
  // mais rótulos do que posições. O `return` existe porque a função devolve texto.
  return rotuloDe(unitKind, existentes.length + 1);
}

/** `"01"` para o apto, que é como a planta o chama; nome e número para os outros. */
function rotuloDe(unitKind: UnitTypeKind, numero: number): string {
  if (unitKind === 'APARTAMENTO') return String(numero).padStart(2, '0');
  return `${UNIT_TYPE_KIND_LABEL[unitKind]} ${String(numero)}`;
}

/** Chave natural do pavimento tipo dentro da obra — o nome normalizado identifica (RN-002). */
export function floorPlanNaturalKey(projectId: ProjectId, name: string): string {
  return `${projectId}|${normalizeName(name)}`;
}

/** Chave natural da posição dentro do pavimento tipo. */
export function unitPositionNaturalKey(floorPlanId: FloorPlanId, label: string): string {
  return `${floorPlanId}|${normalizeName(label)}`;
}

/** Renomear o desenho é ato próprio: o nome é a identidade dele dentro da obra. */
export function renameFloorPlan(plan: FloorPlan, name: string, now: string): FloorPlan {
  const limpo = name.trim();
  if (limpo === plan.name) return plan;
  return { ...plan, name: limpo, updatedAt: now };
}

/** Tira o desenho de circulação sem apagar o passado (`deletedAt`, ADR-105). */
export function deleteFloorPlan(plan: FloorPlan, now: string): FloorPlan {
  return { ...plan, deletedAt: now, updatedAt: now };
}

export function compareUnitPositions(a: UnitPosition, b: UnitPosition): number {
  return a.order - b.order || normalizeName(a.label).localeCompare(normalizeName(b.label), 'pt-BR');
}

/**
 * Quais posições são área comum — o que o rateio precisa saber (RN-163).
 *
 * A conta da geometria não conhece tipologia; conhece posição. Esta é a tradução: quem,
 * entre as posições deste pavimento tipo, é hall e escada em vez de apto.
 */
export function commonAreaPositionIds(
  positions: readonly UnitPosition[],
  unitTypes: readonly UnitType[],
): readonly UnitPositionId[] {
  const commonTypeIds = new Set(
    unitTypes.filter((unitType) => isCommonArea(unitType)).map((unitType) => unitType.id),
  );
  return positions
    .filter((position) => commonTypeIds.has(position.unitTypeId))
    .map((position) => position.id);
}

// ── O prédio, de baixo para cima ────────────────────────────────────────────────

/**
 * O que o diálogo "Adicionar Pavimentos" pergunta.
 *
 * A ordem dos campos **é** a ordem do prédio: subsolo, térreo, garagem, lazer, garden,
 * tipo, cobertura. Garden é **único** por natureza — é o andar dos apartamentos especiais,
 * e não um tipo que se repete; por isso é um liga-desliga, e não uma quantidade.
 */
export interface FloorPlanSpec {
  readonly subsolos: number;
  readonly terreo: boolean;
  readonly garagens: number;
  readonly lazer: boolean;
  readonly garden: boolean;
  readonly tipos: number;
  readonly cobertura: boolean;
}

export interface PlannedFloorPlan {
  readonly name: string;
  readonly level: number;
  readonly kind: FloorPlanKind;
  /** Tipo e garden já nascem com apto; nos outros, quem cadastra decide (RN-179). */
  readonly hasUnits: boolean;
}

/** Quantos pavimentos uma geração aceita de uma vez — o mesmo teto de `planFloorRange`. */
export const MAX_GENERATED_FLOORS = 100;

/**
 * Monta a lista de pavimentos a partir do que o diálogo pediu.
 *
 * Função **pura**: devolve o que **seria** criado, para a tela mostrar a prévia antes de
 * gravar. O nível é sequencial e é ele que ordena — subsolo negativo, térreo zero, o resto
 * subindo. Nome que a obra reconhece: "2º Subsolo", "Térreo", "Garagem 1", "3º Pavimento".
 */
export function planFloorPlans(spec: FloorPlanSpec): readonly PlannedFloorPlan[] {
  const subsolos = Math.max(0, Math.trunc(spec.subsolos));
  const garagens = Math.max(0, Math.trunc(spec.garagens));
  const tipos = Math.max(0, Math.trunc(spec.tipos));

  const planejados: PlannedFloorPlan[] = [];

  // Subsolo conta de baixo para cima: o mais fundo é o de maior número.
  for (let indice = subsolos; indice >= 1; indice -= 1) {
    planejados.push({
      name: `${String(indice)}º Subsolo`,
      level: -indice,
      kind: 'SUBSOLO',
      hasUnits: false,
    });
  }

  let nivel = 0;
  const acima = (name: string, kind: FloorPlanKind): void => {
    planejados.push({ name, level: nivel, kind, hasUnits: alwaysHasUnits(kind) });
    nivel += 1;
  };

  if (spec.terreo) acima('Térreo', 'TERREO');
  for (let indice = 1; indice <= garagens; indice += 1) {
    acima(garagens === 1 ? 'Garagem' : `Garagem ${String(indice)}`, 'GARAGEM');
  }
  if (spec.lazer) acima('Lazer', 'LAZER');
  if (spec.garden) acima('Garden', 'GARDEN');
  for (let indice = 1; indice <= tipos; indice += 1) {
    acima(`${String(nivel)}º Pavimento`, 'TIPO');
  }
  if (spec.cobertura) acima('Cobertura', 'COBERTURA');

  return planejados;
}

/** `"2 subsolos · Térreo · 2 garagens · Garden · 20 tipos · Cobertura"` — a prévia em uma linha. */
export function describeFloorPlanSpec(spec: FloorPlanSpec): string {
  const partes: string[] = [];
  if (spec.subsolos > 0) {
    partes.push(`${String(spec.subsolos)} ${spec.subsolos === 1 ? 'subsolo' : 'subsolos'}`);
  }
  if (spec.terreo) partes.push('Térreo');
  if (spec.garagens > 0) {
    partes.push(`${String(spec.garagens)} ${spec.garagens === 1 ? 'garagem' : 'garagens'}`);
  }
  if (spec.lazer) partes.push('Lazer');
  if (spec.garden) partes.push('Garden');
  if (spec.tipos > 0) {
    partes.push(`${String(spec.tipos)} ${spec.tipos === 1 ? 'tipo' : 'tipos'}`);
  }
  if (spec.cobertura) partes.push('Cobertura');
  return partes.length === 0 ? 'nenhum pavimento' : partes.join(' · ');
}
